import classes from './sparkline.module.css';

interface SparklineProps {
  /** Oldest first. Nulls are gaps — the line stops rather than pretending. */
  values: (number | null)[];
  label: string;
  width?: number;
  height?: number;
}

/** Vertical room left above and below the line, so a steep series still reads as a curve. */
const PADDING = 4;

/**
 * A rung's value across every cohort, small enough to sit beside its number.
 *
 * The funnel answers "where do they stop"; it cannot answer "is this getting
 * better", which is the next thing anybody asks.
 *
 * Scaled to its own range rather than to zero — the question is the direction
 * of travel, and a zero baseline flattens six similar cohorts into a straight
 * line. The area fill and the tinted track are what stop a two-point series
 * reading as a stray diagonal scratch on the page.
 */
export function Sparkline({
  values,
  label,
  width = 72,
  height = 24,
}: SparklineProps) {
  const points = values
    .map((value, index) => ({ value, index }))
    .filter((point): point is { value: number; index: number } =>
      point.value != null,
    );

  if (points.length < 2) return null;

  const numbers = points.map((point) => point.value);
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);
  const span = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : width;

  const coords = points.map((point) => ({
    x: point.index * step,
    y: height - PADDING - ((point.value - min) / span) * (height - PADDING * 2),
  }));

  const line = coords
    .map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`)
    .join(' ');

  const first = coords[0];
  const last = coords[coords.length - 1];
  const area =
    first && last
      ? `${first.x.toFixed(1)},${height} ${line} ${last.x.toFixed(1)},${height}`
      : '';

  return (
    <svg
      role="img"
      aria-label={label}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={classes.spark}
    >
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        rx={3}
        className={classes.track}
      />
      <polygon points={area} className={classes.area} />
      <polyline points={line} className={classes.line} />
      {last && <circle cx={last.x} cy={last.y} r={2} className={classes.head} />}
    </svg>
  );
}
